import { useEffect, useMemo, useState } from 'react';
import {
  FiBriefcase,
  FiEdit2,
  FiFilter,
  FiHash,
  FiPlus,
  FiSearch,
  FiTrash2,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import Api from '../../Api';
import { Button, Card, CompanyForm, Input, Modal, PageComponent, Skeletons, Table, TableActions } from '../../components';
import { usePermission } from '../../hooks/usePermission';
import './style.css';

function getCompanyLogoUrl(company) {
  if (!company || !company.logo) {
    return null;
  }

  if (company.logo.type === 'Buffer' && company.logo.data) {
    const base64String = btoa(
      new Uint8Array(company.logo.data).reduce(
        (data, byte) => data + String.fromCharCode(byte),
        '',
      ),
    );

    return `data:image/png;base64,${base64String}`;
  }

  if (typeof company.logo === 'string') {
    return company.logo;
  }

  return null;
}

export default function CompanyManager() {
  const permissions = usePermission('companies');
  const [user] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('authUser'));
    } catch (e) {
      return null;
    }
  });
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [cnpjFilter, setCnpjFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, cnpjFilter]);

  async function loadCompanies() {
    try {
      setLoading(true);
      const data = await Api.getCompanies();
      if (Array.isArray(data)) {
        setCompanies(data);
      } else {
        setError('Não foi possível carregar as empresas.');
      }
    } catch (err) {
      setError('Erro ao carregar empresas. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  function handleCreate() {
    setSelectedCompany(null);
    setIsModalOpen(true);
  }

  function handleEdit(company) {
    setSelectedCompany(company);
    setIsModalOpen(true);
  }

  function handleFormSuccess() {
    loadCompanies();
    setIsModalOpen(false);
  }

  function handleRequestDelete(company) {
    setCompanyToDelete(company);
    setIsDeleteModalOpen(true);
  }

  async function handleConfirmDelete() {
    if (!companyToDelete) return;

    try {
      setIsDeleting(true);
      const result = await Api.deleteCompany(companyToDelete.id);

      if (result && !result.error) {
        setCompanies(prev => prev.filter(c => c.id !== companyToDelete.id));
        setIsDeleteModalOpen(false);
        setCompanyToDelete(null);
      } else {
        alert(result && result.error ? result.error : 'Erro ao excluir empresa.');
      }
    } catch (err) {
      alert('Erro ao excluir empresa.');
    } finally {
      setIsDeleting(false);
    }
  }

  function handleCancelDelete() {
    if (isDeleting) return;

    setIsDeleteModalOpen(false);
    setCompanyToDelete(null);
  }

  const filteredCompanies = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return companies.filter(company => {
      const matchesSearch =
        !term ||
        (company.corporate_name || '').toLowerCase().includes(term) ||
        (company.trade_name || '').toLowerCase().includes(term) ||
        (company.cnpj || '').toLowerCase().includes(term);

      let matchesCnpj = true;

      if (cnpjFilter === 'with') {
        matchesCnpj = Boolean(company.cnpj);
      } else if (cnpjFilter === 'without') {
        matchesCnpj = !company.cnpj;
      }

      return matchesSearch && matchesCnpj;
    });
  }, [companies, searchTerm, cnpjFilter]);

  const totalItems = filteredCompanies.length;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1;
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedCompanies = filteredCompanies.slice(
    startIndex,
    startIndex + pageSize,
  );

  const columns = [
    {
      id: 'company',
      header: 'Empresa',
      width: '2.2fr',
      cellClassName: 'manager-table-cell-primary',
      render: company => {
        const logoUrl = getCompanyLogoUrl(company);

        return (
          <>
            <div className="manager-table-avatar">
              {logoUrl ? (
                <img src={logoUrl} alt={company.corporate_name} />
              ) : (
                <FiBriefcase size={18} />
              )}
            </div>
            <div className="manager-table-cell-text">
              <span className="manager-table-title">
                {company.corporate_name}
              </span>
              <span className="manager-table-subtitle">
                ID: {company.id}
              </span>
            </div>
          </>
        );
      },
    },
    {
      id: 'trade_name',
      header: 'Nome Fantasia',
      width: '1.8fr',
      cellClassName: 'manager-table-cell',
      render: company => (
        <span className="manager-table-subtitle">
          {company.trade_name || 'Sem nome fantasia'}
        </span>
      ),
    },
    {
      id: 'cnpj',
      header: 'CNPJ',
      width: '1.4fr',
      cellClassName: 'manager-table-cell manager-table-col-cnpj',
      render: company => (
        <div className="manager-table-cnpj">
          <FiHash size={14} />
          <span>{company.cnpj || 'Não informado'}</span>
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      width: '1.4fr',
      cellClassName: 'manager-table-cell manager-table-col-tags',
      render: company => {
        const hasCnpj = Boolean(company.cnpj);

        return (
          <div className="manager-tags">
            <span
              className={[
                'manager-tag',
                hasCnpj ? 'manager-tag-success' : 'manager-tag-warning',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {hasCnpj ? 'CNPJ cadastrado' : 'Sem CNPJ'}
            </span>
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: 'Ações',
      width: '0.6fr',
      cellClassName: 'manager-table-cell manager-table-col-actions',
      render: company => {
        const canEdit = permissions.canEdit;
        const canDelete = permissions.canDelete;

        if (!canEdit && !canDelete) return null;

        return (
          <TableActions>
            {canEdit && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(company);
                }}
              >
                <FiEdit2 size={16} />
                Editar
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                className="action-danger"
                disabled={user && user.role === 'subuser' && user.access_profile !== 'admin'}
                style={user && user.role === 'subuser' && user.access_profile !== 'admin' ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                title={user && user.role === 'subuser' && user.access_profile !== 'admin' ? 'Apenas administradores podem excluir empresas' : 'Excluir'}
                onClick={(e) => {
                  e.stopPropagation();
                  if (user && user.role === 'subuser' && user.access_profile !== 'admin') return;
                  handleRequestDelete(company);
                }}
              >
                <FiTrash2 size={16} />
                Excluir
              </button>
            )}
          </TableActions>
        );
      },
    },
  ];

  function CompanyManagerSkeletonWrapper() {
    const { CompanyManagerSkeleton } = Skeletons;
    return <CompanyManagerSkeleton />;
  }

  if (!permissions.canView) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiBriefcase size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar esta página.</p>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    );
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
        {loading && (
          <CompanyManagerSkeletonWrapper />
        )}

        {!loading && (
          <>
            <div className="manager-header">
              <div className="manager-header-title">
                <div className="manager-header-icon">
                  <FiBriefcase size={22} />
                </div>
                <div>
                  <h1>Empresas</h1>
                  <p>Visualize, filtre e gerencie todas as empresas cadastradas.</p>
                </div>
              </div>
              {permissions.canCreate && (
                <Button onClick={handleCreate} className="manager-header-button">
                  <FiPlus size={18} />
                  Nova Empresa
                </Button>
              )}
            </div>

            <Card className="manager-filters-card">
              <div className="manager-filters-header">
                <div className="manager-filters-title">
                  <FiFilter size={16} />
                  <span>Filtros</span>
                </div>
                {companies.length > 0 && (
                  <span className="manager-filters-count">
                    {filteredCompanies.length} de {companies.length} empresas
                  </span>
                )}
              </div>

              <div className="manager-filters-grid">
                <div className="manager-filter-group">
                  <label className="manager-filter-label">Buscar</label>
                  <div className="manager-filter-search">
                    <Input
                      id="company-search"
                      placeholder="Busque por razão social, nome fantasia ou CNPJ"
                      value={searchTerm}
                      onChange={event => setSearchTerm(event.target.value)}
                      icon={FiSearch}
                    />
                  </div>
                </div>

                <div className="manager-filter-group">
                  <label className="manager-filter-label">CNPJ</label>
                  <div className="manager-filter-chips">
                    <button
                      type="button"
                      className={[
                        'manager-chip',
                        cnpjFilter === 'all' && 'manager-chip-active',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => setCnpjFilter('all')}
                    >
                      Todos
                    </button>
                    <button
                      type="button"
                      className={[
                        'manager-chip',
                        cnpjFilter === 'with' && 'manager-chip-active',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => setCnpjFilter('with')}
                    >
                      Com CNPJ
                    </button>
                    <button
                      type="button"
                      className={[
                        'manager-chip',
                        cnpjFilter === 'without' && 'manager-chip-active',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => setCnpjFilter('without')}
                    >
                      Sem CNPJ
                    </button>
                  </div>
                </div>
              </div>
            </Card>

            {error && <div className="error">{error}</div>}

            {!loading && !error && companies.length === 0 && (
              <div className="empty-state">
                <p>Nenhuma empresa cadastrada.</p>
              </div>
            )}

            {!loading && !error && companies.length > 0 && (
              <Table
                items={paginatedCompanies}
                columns={columns}
                totalItems={totalItems}
                page={safePage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={value => {
                  setPageSize(value);
                  setCurrentPage(1);
                }}
                getRowKey={company => company.id}
                emptyMessage="Nenhuma empresa encontrada com os filtros atuais."
              />
            )}
          </>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedCompany ? 'Editar Empresa' : 'Nova Empresa'}
        closeOnOverlayClick={false}
      >
        <CompanyForm
          initialData={selectedCompany}
          onSuccess={handleFormSuccess}
          onCancel={() => setIsModalOpen(false)}
        />
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDelete}
        title="Confirmar exclusão"
        closeOnOverlayClick={!isDeleting}
      >
        <div className="delete-modal-content">
          <p>
            Tem certeza que deseja excluir a empresa{' '}
            {companyToDelete && (
              <span className="delete-modal-company-name">
                {companyToDelete.corporate_name}
              </span>
            )}
            ?
          </p>
          <p>
            Essa ação não poderá ser desfeita.
          </p>
        </div>
        <div className="delete-modal-actions">
          <Button
            type="button"
            className="cancel-button"
            onClick={handleCancelDelete}
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Excluindo...' : 'Excluir'}
          </Button>
        </div>
      </Modal>
    </PageComponent>
  );
}
